// Credit pricing definitions, model costs, and complexity multiplier logic

// === PER-MODEL REAL API PRICING (USD) ===
// Sources: Google AI, OpenAI, ElevenLabs published pricing
export interface ModelPricing {
  input_per_1m: number;   // USD per 1M input tokens
  output_per_1m: number;  // USD per 1M output tokens
  category: "text" | "image" | "voice" | "utility";
  label: string;          // human-friendly name
  per_call?: number;      // USD per call (images, SERP, etc.)
  per_1k_chars?: number;  // USD per 1K characters (TTS)
}

export const MODEL_PRICING: Record<string, ModelPricing> = {
  // ── Text Models ──
  "google/gemini-2.5-pro":             { input_per_1m: 1.25,  output_per_1m: 10.00, category: "text",  label: "Gemini 2.5 Pro" },
  "google/gemini-3.1-pro-preview":     { input_per_1m: 1.25,  output_per_1m: 10.00, category: "text",  label: "Gemini 3.1 Pro" },
  "google/gemini-3-flash-preview":     { input_per_1m: 0.15,  output_per_1m: 0.60,  category: "text",  label: "Gemini 3 Flash" },
  "google/gemini-2.5-flash":           { input_per_1m: 0.15,  output_per_1m: 0.60,  category: "text",  label: "Gemini 2.5 Flash" },
  "google/gemini-2.5-flash-lite":      { input_per_1m: 0.075, output_per_1m: 0.30,  category: "text",  label: "Gemini 2.5 Flash Lite" },
  "openai/gpt-5":                      { input_per_1m: 10.00, output_per_1m: 30.00, category: "text",  label: "GPT-5" },
  "openai/gpt-5-mini":                 { input_per_1m: 1.50,  output_per_1m: 6.00,  category: "text",  label: "GPT-5 Mini" },
  "openai/gpt-5-nano":                 { input_per_1m: 0.50,  output_per_1m: 2.00,  category: "text",  label: "GPT-5 Nano" },
  "openai/gpt-5.2":                    { input_per_1m: 12.00, output_per_1m: 40.00, category: "text",  label: "GPT-5.2" },
  // ── Image Models ──
  "google/gemini-2.5-flash-image":         { input_per_1m: 0.15, output_per_1m: 0.60, category: "image", label: "Gemini Flash Image", per_call: 0.04 },
  "google/gemini-3-pro-image-preview":     { input_per_1m: 1.25, output_per_1m: 10.00, category: "image", label: "Gemini 3 Pro Image", per_call: 0.07 },
  "google/gemini-3.1-flash-image-preview": { input_per_1m: 0.15, output_per_1m: 0.60, category: "image", label: "Gemini 3.1 Flash Image", per_call: 0.04 },
  // ── Voice / TTS ──
  "inworld-tts-1.5-max":  { input_per_1m: 0, output_per_1m: 0, category: "voice", label: "Inworld TTS 1.5", per_1k_chars: 0.02 },
  "eleven_turbo_v2_5":    { input_per_1m: 0, output_per_1m: 0, category: "voice", label: "ElevenLabs Turbo v2.5", per_1k_chars: 0.03 },
  "eleven_flash_v2_5":    { input_per_1m: 0, output_per_1m: 0, category: "voice", label: "ElevenLabs Flash v2.5", per_1k_chars: 0.025 },
};

// Fallback for unknown models
export const FALLBACK_PRICING: ModelPricing = { input_per_1m: 0.15, output_per_1m: 0.60, category: "text", label: "Unknown" };

// Utility service costs
export const SERP_COST_PER_CALL = 0.005;   // USD per search
export const PROXY_COST_PER_CALL = 0.001;  // USD per fetch

/** Get real USD cost for a single usage log entry */
export function getModelCostUsd(
  model: string | null,
  costType: string,
  inputTokens: number,
  outputTokens: number,
  inputChars: number,
): number {
  const m = model || "unknown";
  const pricing = MODEL_PRICING[m] || FALLBACK_PRICING;

  if (costType === "voice") return (inputChars / 1000) * (pricing.per_1k_chars ?? 0.025);
  if (costType === "image" && pricing.per_call) return pricing.per_call;
  if (costType === "serp") return SERP_COST_PER_CALL;
  if (costType === "proxy") return PROXY_COST_PER_CALL;

  return (inputTokens / 1_000_000) * pricing.input_per_1m
       + (outputTokens / 1_000_000) * pricing.output_per_1m;
}

// === PLATFORM CREDIT RATES ===
// How many raw units correspond to 1 credit (platform markup applied)
export const TOKEN_RATES = {
  text_input_per_credit:  66_667,    // input tokens per credit
  text_output_per_credit: 16_667,    // output tokens per credit
  voice_chars_per_credit: 333,       // characters per credit
  images_per_credit: 0.25,           // images per credit (1 image = 4 credits)
  serp_per_credit: 2,                // searches per credit
  proxy_per_credit: 10,              // fetches per credit
} as const;

/** Calculate credits consumed from raw usage numbers */
export function creditsFromUsage(
  textInputTokens: number,
  textOutputTokens: number,
  voiceChars: number,
  imageCalls: number,
  serpCalls: number = 0,
  proxyCalls: number = 0,
): number {
  return (
    textInputTokens / TOKEN_RATES.text_input_per_credit +
    textOutputTokens / TOKEN_RATES.text_output_per_credit +
    voiceChars / TOKEN_RATES.voice_chars_per_credit +
    imageCalls / TOKEN_RATES.images_per_credit +
    serpCalls / TOKEN_RATES.serp_per_credit +
    proxyCalls / TOKEN_RATES.proxy_per_credit
  );
}

/** Statistical average: credits per single LLM call (based on typical adventure turn) */
export const AVG_TOKENS_PER_TURN = {
  input: 4000,
  output: 800,
};

export function avgCreditsPerTurn(): number {
  return creditsFromUsage(AVG_TOKENS_PER_TURN.input, AVG_TOKENS_PER_TURN.output, 0, 0);
}

// === BUILDER ACTIONS (credits per action) ===
export const BUILDER_CREDITS = {
  add_entity:      1,
  add_mechanic:    1,
  add_element:     1,
  planning_read:   1,
  edit_entity:     0.5,
  edit_mechanic:   0.5,
  edit_element:    0.5,
} as const;

// === GAMEPLAY ACTIONS (credits per action, before complexity multiplier) ===
export const GAMEPLAY_CREDITS = {
  single_message:  1,
} as const;

export type BuilderAction = keyof typeof BUILDER_CREDITS;
export type GameplayAction = keyof typeof GAMEPLAY_CREDITS;

// === COMPLEXITY MULTIPLIER ===
const MAX_SCORE = 200;

export function calculateComplexity(activeMechanicsCount: number, entityCount: number): number {
  const weightedScore = activeMechanicsCount * 3 + entityCount * 0.1;
  const clamped = Math.min(weightedScore, MAX_SCORE);
  const normalized = Math.log(1 + clamped) / Math.log(1 + MAX_SCORE);
  return Math.round((0.1 + 1.9 * normalized) * 100) / 100;
}

export function getComplexityLabel(multiplier: number): string {
  if (multiplier <= 0.5) return "Minimal";
  if (multiplier <= 1.0) return "Low";
  if (multiplier <= 1.5) return "Moderate";
  return "High";
}

export function getComplexityColor(multiplier: number): string {
  if (multiplier <= 0.5) return "text-green-500";
  if (multiplier <= 1.0) return "text-emerald-500";
  if (multiplier <= 1.5) return "text-yellow-500";
  return "text-orange-500";
}

/** Calculate effective gameplay credits after complexity multiplier */
export function gameplayCredits(action: GameplayAction, complexityMultiplier: number): number {
  return Math.round(GAMEPLAY_CREDITS[action] * complexityMultiplier * 100) / 100;
}
